import BoxKit from "@/shared/components/kit/box";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import { useState } from "react";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, } from "chart.js";
import { Line } from "react-chartjs-2";
import CachedOutlinedIcon from "@mui/icons-material/CachedOutlined";
import { fetchNoduedateCreditBalanceChart } from "@/api/merchant/request.get";
import { useQuery } from "@tanstack/react-query";
import { numberSeparator, toPersianNumber } from "@/hooks/useThousandsSeparator";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);
/**
 * LineChart
 *
 * نمایش نمودار خطی موجودی کیف پول با داده‌های روزانه یا قابل برداشت.
 *
 * @param selectedNumber انتخاب نوع داده برای نمودار: "all" برای کل موجودی یا سایر مقادیر برای موجودی قابل برداشت.
 * @returns کامپوننت React حاوی نمودار خطی و دکمه بروزرسانی.
 */

const LineChart = ({ selectedNumber }: { selectedNumber: string }) => {
  const [typeChart, setTypeChart] = useState("daily");

  const {
    data: dataNoduedateCreditBalanceChart,
    isFetched,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["noduedateCred-data", selectedNumber],
    enabled: selectedNumber === "all",
    queryFn: fetchNoduedateCreditBalanceChart,
  });

  const chartData =
    dataNoduedateCreditBalanceChart?.all?.chart?.original?.data?.chart;

  const labels =
    chartData && Array.isArray(chartData)
      ? chartData.map((item) => {
        const [year, month, day] = item.date.split("-");
        return toPersianNumber(`${parseInt(month)}/${parseInt(day)}`);
      })
      : [];

  const dataValues =
    chartData && Array.isArray(chartData)
      ? chartData.map((item) => item.credit)
      : [];
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top" as const,
        labels: {
          font: {
            family: "vaziriFont",
          },
          color: "#fff",
        },
      },
      title: {
        display: true,
        text: "",
        font: {
          family: "vaziriFont",
          size: 16,
        },
        color: "#fff",
      },
      tooltip: {
        titleFont: {
          family: "vaziriFont",
        },
        bodyFont: {
          family: "vaziriFont",
        },
        callbacks: {
          label: function (context: any) {
            const value = context.parsed.y;
            return `مقدار: ${toPersianNumber(numberSeparator(value))}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(243, 243, 243, 0.5)",
        },
        ticks: {
          font: {
            family: "vaziriFont",
          },
          color: "#fff",
          callback: function (value: any) {
            return `${toPersianNumber(numberSeparator(value))} ریال`;
          },
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            family: "vaziriFont",
          },
          color: "#fff",
        },
      },
    },
    elements: {
      line: {
        tension: 0,
      },
      point: {
        radius: 4,
        hoverRadius: 6,
      },
    },
  };

  const data = {
    labels: labels.length > 0
      ? labels
      : ["1/1", "2/1", "3/1", "4/1", "5/1", "6/1", "7/1", "8/1", "9/1"], // fallback
    datasets: [
      {
        label: "",
        data: dataValues.length > 0
          ? dataValues
          : [
            450000000, 520000000, 380000000,
            610000000, 720000000, 580000000,
            690000000, 750000000, 820000000,
          ],
        borderColor: "#EB6440",
        backgroundColor: "#EB6440",
        borderWidth: 2,
        fill: false,
        pointBackgroundColor: "#EB6440",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
      },
    ],
  };

  const handleRefresh = () => {
    refetch();
  };

  return (
    <StackKit>
      <StackKit direction={{ xs: "column", sm: "row" }} gap={{ xs: 1, sm: 2 }} alignItems={"center"}>
        <TypoGraphyKit variant={'body1'}>
          {selectedNumber === "all" ? "موجودی کل کیف پول سررسید نشده در حال حاضر" : " موجودی کل کیف پول قابل برداشت در حال حاضر"}
        </TypoGraphyKit>
        <StackKit direction={"row"} gap={1} alignItems={"center"}>
          <TypoGraphyKit variant={"h6"}>
            {numberSeparator(dataNoduedateCreditBalanceChart?.all?.credit)}
          </TypoGraphyKit>
          <TypoGraphyKit variant="body1">ریال</TypoGraphyKit>
        </StackKit>
      </StackKit>
      <BoxKit sx={{ width: "100%", maxWidth: "100%", height: { xs: "300px", sm: "350px", md: "400px" } }}>
        <Line data={data} options={options} />
      </BoxKit>
    </StackKit>
  );
};

export default LineChart;
